import { DashboardStats } from "./components/DashboardStats";
import { PassFailPieChart } from "./components/PassFailPieChart";
import { WelderStatusBarChart } from "./components/WelderStatusBarChart";
import { InspectionVolumeTrend } from "./components/InspectionVolumeTrend";
import { StandardTestPieChart } from "./components/StandardTestPieChart";
import { useDashboardStats } from "./hooks";
import { MantineProvider } from "@mantine/core";
import "@mantine/core/styles.css";
import "@mantine/charts/styles.css";

export function HomeView() {
  const { data: stats } = useDashboardStats();

  const inServiceFailRate = 100 - (stats?.inService.passFailRate ?? 0);
  const inServicePassRate = stats?.inService.passFailRate ?? 0;
  const inServiceWelderTests = stats?.inService.welderTests ?? 0;
  const inServiceActiveWelders = stats?.inService.activeWelders ?? 0;
  const inServiceWeldersAllowedToExpire = stats?.inService.weldersAllowedToExpire ?? 0;
  const inServiceWeldersRevoked = stats?.inService.weldersRevoked ?? 0;
  const inServiceWeldersDisqualified = stats?.inService.weldersDisqualified ?? 0;

  const welderStatusData = [
    { status: "Active", value: inServiceActiveWelders },
    { status: "Expired", value: inServiceWeldersAllowedToExpire },
    { status: "Revoked", value: inServiceWeldersRevoked },
    { status: "Disqualified", value: inServiceWeldersDisqualified },
  ];

  return (
    <MantineProvider>
      <>
        <div className="border-start border-4 border-primary ps-3 mb-3">
          <h4 className="text-primary mb-1">In Service KPIs</h4>
          <p className="text-muted mb-0 small">Active welders currently qualified and working in the field</p>
        </div>

        <div className="d-flex flex-column gap-3">
          <DashboardStats
            passRate={inServicePassRate}
            totalTests={inServiceWelderTests}
            activeWelders={inServiceActiveWelders}
            weldersExpired={inServiceWeldersAllowedToExpire}
          />

          <div className="row g-3">
            <div className="col-12 col-md-4 d-flex">
              <div className="card border flex-fill d-flex flex-column">
                <div className="card-body d-flex flex-column">
                  <h5 className="card-title mb-2">IN SERVICE PASS / FAIL RATE</h5>
                  <PassFailPieChart passRate={inServicePassRate} failRate={inServiceFailRate} />
                </div>
              </div>
            </div>
            <div className="col-12 col-md-8 d-flex">
              <div className="card border flex-fill d-flex flex-column">
                <div className="card-body d-flex flex-column">
                  <h5 className="card-title mb-2">IN SERVICE WELDER STATUS BREAKDOWN</h5>
                  <WelderStatusBarChart data={welderStatusData} />
                </div>
              </div>
            </div>
          </div>

          <div className="card border">
            <div className="card-body">
              <h5 className="card-title mb-2">IN SERVICE INSPECTION VOLUME TREND</h5>
              <InspectionVolumeTrend data={stats?.inspector || []} />
            </div>
          </div>

          <div className="card border">
            <div className="card-body">
              <h5 className="card-title mb-2">IN SERVICE STANDARD TEST FREQUENCY</h5>
              <StandardTestPieChart data={stats?.standardTests} />
            </div>
          </div>

          <hr className="my-4" />

          <div className="border-start border-4 border-primary ps-3 mb-3">
            <h4 className="text-primary mb-1">Data sets</h4>
            <p className="text-muted mb-0 small">Raw data for the KPIs above</p>
          </div>

          <div className="card border">
            <div className="card-body">
              <h5 className="card-title mb-2">WELDERS & IN SERVICE STATISTICS</h5>
              <div className="table-responsive">
                <table className="table table-striped table-hover w-100">
                  <thead>
                    <tr>
                      <th style={{ width: "50%" }}>Metric</th>
                      <th style={{ width: "25%" }} className="text-center">
                        Welders
                      </th>
                      <th style={{ width: "25%" }} className="text-center">
                        In Service
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Pass rate</td>
                      <td className="text-center">{stats?.welder.passFailRate} %</td>
                      <td className="text-center">{stats?.inService.passFailRate} %</td>
                    </tr>
                    <tr>
                      <td>Total welder tests</td>
                      <td className="text-center">{stats?.welder.welderTests}</td>
                      <td className="text-center">{stats?.inService.welderTests}</td>
                    </tr>
                    <tr>
                      <td>Total welders tested</td>
                      <td className="text-center">{stats?.welder.weldersTested}</td>
                      <td className="text-center">{stats?.inService.weldersTested}</td>
                    </tr>
                    <tr>
                      <td>Total active welders</td>
                      <td className="text-center">{stats?.welder.activeWelders}</td>
                      <td className="text-center">{stats?.inService.activeWelders}</td>
                    </tr>
                    <tr>
                      <td>Total welders allowed to expire</td>
                      <td className="text-center">{stats?.welder.weldersAllowedToExpire}</td>
                      <td className="text-center">{stats?.inService.weldersAllowedToExpire}</td>
                    </tr>
                    <tr>
                      <td>Welders revoked</td>
                      <td className="text-center">{stats?.welder.weldersRevoked}</td>
                      <td className="text-center">{stats?.inService.weldersRevoked}</td>
                    </tr>
                    <tr>
                      <td>Welders disqualified</td>
                      <td className="text-center">{stats?.welder.weldersDisqualified}</td>
                      <td className="text-center">{stats?.inService.weldersDisqualified}</td>
                    </tr>
                    <tr>
                      <td>Standard test frequency</td>
                      <td className="text-center">{stats?.welder.testFrequency}</td>
                      <td className="text-center">{stats?.inService.testFrequency}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="card border">
            <div className="card-body">
              <h5 className="card-title mb-2">WELDING INSPECTOR TESTS</h5>

              {/* Desktop Table View */}
              <div className="table-responsive d-none d-md-block" style={{ maxHeight: "400px" }}>
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th style={{ width: "25%", maxWidth: "300px" }}>Inspector</th>
                      <th style={{ width: "6.25%" }} className="text-center">
                        Jan
                      </th>
                      <th style={{ width: "6.25%" }} className="text-center">
                        Feb
                      </th>
                      <th style={{ width: "6.25%" }} className="text-center">
                        Mar
                      </th>
                      <th style={{ width: "6.25%" }} className="text-center">
                        Apr
                      </th>
                      <th style={{ width: "6.25%" }} className="text-center">
                        May
                      </th>
                      <th style={{ width: "6.25%" }} className="text-center">
                        Jun
                      </th>
                      <th style={{ width: "6.25%" }} className="text-center">
                        Jul
                      </th>
                      <th style={{ width: "6.25%" }} className="text-center">
                        Aug
                      </th>
                      <th style={{ width: "6.25%" }} className="text-center">
                        Sep
                      </th>
                      <th style={{ width: "6.25%" }} className="text-center">
                        Oct
                      </th>
                      <th style={{ width: "6.25%" }} className="text-center">
                        Nov
                      </th>
                      <th style={{ width: "6.25%" }} className="text-center">
                        Dec
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats?.inspector.map((inspector) => (
                      <tr key={inspector.id}>
                        <td className="fw-medium">{inspector.name}</td>
                        <td className="text-center">{inspector.january}</td>
                        <td className="text-center">{inspector.february}</td>
                        <td className="text-center">{inspector.march}</td>
                        <td className="text-center">{inspector.april}</td>
                        <td className="text-center">{inspector.may}</td>
                        <td className="text-center">{inspector.june}</td>
                        <td className="text-center">{inspector.july}</td>
                        <td className="text-center">{inspector.august}</td>
                        <td className="text-center">{inspector.september}</td>
                        <td className="text-center">{inspector.october}</td>
                        <td className="text-center">{inspector.november}</td>
                        <td className="text-center">{inspector.december}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="d-md-none">
                <div className="d-flex flex-column gap-3">
                  {stats?.inspector.map((inspector) => (
                    <div key={inspector.id} className="card border">
                      <div className="card-header border-bottom">
                        <div className="fw-medium">{inspector.name}</div>
                      </div>
                      <div className="card-body">
                        <div className="row gy-3">
                          <div className="col-12">
                            <div className="row justify-content-between">
                              <div className="col-2" style={{ width: "100px" }}>
                                <div className="d-flex align-items-center justify-content-between">
                                  <small className="text-muted">Jan:</small>
                                  <small>{inspector.january}</small>
                                </div>
                              </div>
                              <div className="col-2" style={{ width: "100px" }}>
                                <div className="d-flex align-items-center justify-content-between">
                                  <small className="text-muted">Feb:</small>
                                  <small>{inspector.february}</small>
                                </div>
                              </div>
                              <div className="col-2" style={{ width: "100px" }}>
                                <div className="d-flex align-items-center justify-content-between">
                                  <small className="text-muted">Mar:</small>
                                  <small>{inspector.march}</small>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-12">
                            <div className="row justify-content-between">
                              <div className="col-2" style={{ width: "100px" }}>
                                <div className="d-flex align-items-center justify-content-between">
                                  <small className="text-muted">Apr:</small>
                                  <small>{inspector.april}</small>
                                </div>
                              </div>
                              <div className="col-2" style={{ width: "100px" }}>
                                <div className="d-flex align-items-center justify-content-between">
                                  <small className="text-muted">May:</small>
                                  <small>{inspector.may}</small>
                                </div>
                              </div>
                              <div className="col-2" style={{ width: "100px" }}>
                                <div className="d-flex align-items-center justify-content-between">
                                  <small className="text-muted">Jun:</small>
                                  <small>{inspector.june}</small>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-12">
                            <div className="row justify-content-between">
                              <div className="col-2" style={{ width: "100px" }}>
                                <div className="d-flex align-items-center justify-content-between">
                                  <small className="text-muted">Jul:</small>
                                  <small>{inspector.july}</small>
                                </div>
                              </div>
                              <div className="col-2" style={{ width: "100px" }}>
                                <div className="d-flex align-items-center justify-content-between">
                                  <small className="text-muted">Aug:</small>
                                  <small>{inspector.august}</small>
                                </div>
                              </div>
                              <div className="col-2" style={{ width: "100px" }}>
                                <div className="d-flex align-items-center justify-content-between">
                                  <small className="text-muted">Sep:</small>
                                  <small>{inspector.september}</small>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-12">
                            <div className="row justify-content-between">
                              <div className="col-2" style={{ width: "100px" }}>
                                <div className="d-flex align-items-center justify-content-between">
                                  <small className="text-muted">Oct:</small>
                                  <small>{inspector.october}</small>
                                </div>
                              </div>
                              <div className="col-2" style={{ width: "100px" }}>
                                <div className="d-flex align-items-center justify-content-between">
                                  <small className="text-muted">Nov:</small>
                                  <small>{inspector.november}</small>
                                </div>
                              </div>
                              <div className="col-2" style={{ width: "100px" }}>
                                <div className="d-flex align-items-center justify-content-between">
                                  <small className="text-muted">Dec:</small>
                                  <small>{inspector.december}</small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    </MantineProvider>
  );
}
